import React from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import './PlayerCard.css';

/**
 * PlayerCard Component
 * Displays player profile information matching the template design
 * Features orange/black theme with bold typography
 */

const PlayerCard = ({ player }) => {
  const navigate = useNavigate();

  // Parse badges from comma-separated string
  const badges = player.badges || [];

  // Default photo if none provided
  const photoUrl = player.Photo_URL 
    ? ApiService.getPlayerPhotoUrl(player.Photo_URL)
    : '/photos/Unknown.png';

  // Format record with color coding
  const formatRecord = () => {
    const wins = player.Wins || 0;
    const undecided = player.Undecided || 0;
    const losses = player.Losses || 0;
    
    return (
      <span className="record">
        <span className="wins">{wins}W</span>
        <span className="separator">-</span>
        <span className="undecided">{undecided}U</span>
        <span className="separator">-</span>
        <span className="losses">{losses}L</span>
      </span>
    );
  };

  const handleClick = () => {
    navigate(`/player/${player.Player_ID}`);
  };

  return (
    <div className="player-card" onClick={handleClick}>
      <div className="player-card-header">
        <h2 className="player-name">{player.Player_Name || player.Player_ID}</h2>
        <div className="player-ranking">
          {player.Ranking ? `${player.Ranking}${getOrdinalSuffix(player.Ranking)}` : 'Unranked'}
        </div>
      </div>

      <div className="player-card-body">
        <div className="player-photo-section">
          <div className="player-photo">
            <img 
              src={photoUrl} 
              alt={player.Player_Name}
              onError={(e) => e.target.src = '/photos/Unknown.png'}
            />
          </div>
        </div>

        <div className="player-info-section">
          <div className="info-grid">
            <div className="info-item">
              <label>Age</label>
              <span>{player.Age || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Country</label>
              <span>{player.Country || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>State</label>
              <span>{player.State || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Height</label>
              <span>{player.Height || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Weight</label>
              <span>{player.Weight || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="player-card-stats">
        <div className="stat-item">
          <label>Record</label>
          {formatRecord()}
        </div>
        <div className="stat-item">
          <label>Class</label>
          <span>{player.Class || 'N/A'}</span>
        </div>
        <div className="stat-item">
          <label>Main Platform</label>
          <span>{player.Main_Platform || 'N/A'}</span>
        </div>
        <div className="stat-item">
          <label>Highest Level</label>
          <span>{player.Highest_Level || 'N/A'}</span>
        </div>
      </div>

      {badges.length > 0 && (
        <div className="player-badges">
          <label>Badges</label>
          <div className="badges-container">
            {badges.map((badgeId) => (
              <div key={badgeId} className="badge">
                <img 
                  src={ApiService.getBadgeImageUrl(badgeId)} 
                  alt={`Badge ${badgeId}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for ordinal suffix
const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

export default PlayerCard;
